import { computed, inject } from 'vue';
import { loadingStateInjectionKey } from '../internal';
import { computeAriaBindings, computeSrText } from './inputSaveStatus';
import { useLoadingStateRegistration } from './useLoadingStateRegistration';

/**
 * Consume the save status provided by an ancestor `NxLoadingWrapper` and expose
 * it for a wrapper-less control. The child registers with the wrapper because it
 * paints the status inline, so the wrapper suppresses its own fallback icon while
 * the child is mounted and active. When there is no wrapper the injection is null
 * and the state stays `idle`.
 */
export function useInputSaveStatus() {
  const injected = inject(loadingStateInjectionKey, null);
  useLoadingStateRegistration(injected);

  const state = computed(() => injected?.status.value.state ?? 'idle');
  const visualState = computed(() => {
    if (injected?.successPresentation === 'border' && state.value === 'success') return 'idle';
    if (injected?.errorPresentation === 'border' && state.value === 'error') return 'idle';
    return state.value;
  });
  const disabled = computed(() => injected?.status.value.disabled ?? false);
  const errorMessage = computed(() => injected?.status.value.errorMessage);
  const fadeOut = computed(() => injected?.status.value.fadeOut ?? false);
  const srText = computed(() => computeSrText(state.value, errorMessage.value));
  const ariaBindings = computed(() => computeAriaBindings(state.value));
  const hasStatus = computed(() => injected != null);

  return { state, visualState, disabled, errorMessage, fadeOut, srText, ariaBindings, hasStatus };
}
